import React, { useMemo, useRef, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AiOutlineCalendar,
  AiOutlineClose,
  AiOutlineLeft,
  AiOutlineRight,
  AiOutlineDelete,
  AiOutlineUser,
  AiOutlineShop,
  AiOutlineClockCircle,
  AiOutlinePartition,
  AiOutlineEnvironment,
  AiOutlineCheckCircle,
} from "react-icons/ai";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import timeGridPlugin from "@fullcalendar/timegrid";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import moment from "moment";

import { fmtBR, formatViewTitle, ISO_FMT, toISO } from "./FullCalendarUtils";
import { getHolidayByDate } from "./Holidays";
import { useComercial } from "../../contexts/ComercialContext";
import ConfirmDeleteDialog from "../UI/ConfirmDeleteDialog";

import "../../styles/fullcalendarCustom.css";

// Cor do evento pelo STATUS da atividade:
// pendente = amarela · concluída = verde · cancelada = vermelha
const STATUS_COLORS = { pendente: '#ECC94B', concluido: '#02EA75', cancelado: '#FC8181' };
const statusColorDe = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('conclu')) return STATUS_COLORS.concluido;
  if (s.includes('cancel')) return STATUS_COLORS.cancelado;
  return STATUS_COLORS.pendente;
};


// Campo (label + valor) usado na modal de detalhes
const InfoField = ({ icon, label, value }) => (
  <Box>
    <HStack spacing={1.5} mb={1}>
      {icon && <Icon as={icon} color="textMuted" boxSize="13px" />}
      <Text fontSize="10px" color="textMuted" textTransform="uppercase" letterSpacing="1px" fontWeight="800">
        {label}
      </Text>
    </HStack>
    <Text fontWeight={700} color="textPrimary" fontSize="sm">{value || '—'}</Text>
  </Box>
);


export default function FullCalendarWithModal() {
  const calendarRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [viewTitle, setViewTitle] = useState("");
  // Visão atual do calendário: nas visões com grade de horário (Dia/Semana) as
  // atividades COM hora viram eventos posicionados no horário certo, em vez de
  // caírem todas na faixa "dia inteiro".
  const [viewName, setViewName] = useState("dayGridMonth");

  const initialDate = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const dateParam = params.get("date");
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      return dateParam;
    }
    return undefined;
  }, [location.search]);

  useEffect(() => {
    const api = calendarRef.current?.getApi?.();
    if (api && initialDate) {
      setTimeout(() => {
        api.gotoDate(initialDate);
      }, 50);
    }
  }, [initialDate]);
  const { atividades, compromissos, deleteCompromisso, pipelineCards } = useComercial();

  const [holidayPopover, setHolidayPopover] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const hojeISO = moment().format(ISO_FMT); // hoje no fuso LOCAL (bloqueio retroativo)

  const { isOpen: detailsOpen, onOpen: onDetailsOpen, onClose: onDetailsClose } = useDisclosure();

  // Confirmação padrão de exclusão do compromisso do calendário
  const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();

  const handleConfirmarExcluirEvento = async () => {
    const compromissoId = selectedEvent?.extendedProps?.compromissoId;
    if (!compromissoId) {
      onDelClose();
      return;
    }
    try {
      await deleteCompromisso(compromissoId);
      onDelClose();
      onDetailsClose();
    } catch (err) {
      console.error("Erro ao excluir:", err);
      onDelClose();
    }
  };

  // Atividades do Comercial → SEGMENTOS (1 evento por dia), iguais ao schedule.
  // Multi-dia é quebrado em N eventos de 1 dia que se "colam" visualmente via CSS
  // (--seg-bleed + classes nogle-seg-*). Isso evita o problema de transbordo entre
  // células do FullCalendar (que fazia a barra parar no 1º dia).
  // EXCEÇÃO (visões Dia/Semana): atividade COM hora vira UM evento com horário
  // real (allDay=false), posicionado na grade — antes tudo caía em "dia inteiro".
  const isTimeGridView = viewName.startsWith('timeGrid');
  const events = useMemo(() => {
    const segs = [];

    (atividades || [])
      .filter(atv => atv.dataInicio)
      .forEach(atv => {
        const startISO = atv.dataInicio;
        const endISO = atv.dataFim || atv.dataInicio; // INCLUSIVO (último dia)
        const start = moment(startISO, ISO_FMT, true);
        const endM = moment(endISO, ISO_FMT, true);
        if (!start.isValid()) return;

        const safeEnd = endM.isValid() && endM.isSameOrAfter(start, "day") ? endM : start;
        let dayCount = safeEnd.diff(start, "days") + 1;
        if (!Number.isFinite(dayCount) || dayCount <= 0) dayCount = 1;

        const color = statusColorDe(atv.completed ? 'concluido' : atv.status);
        const userName = atv.responsavel || 'Comercial';
        const horaInicio = (atv.horaInicio && atv.horaInicio !== '00:00') ? atv.horaInicio : null;
        const horaFim = (atv.horaFim && atv.horaFim !== '00:00') ? atv.horaFim : null;

        const propsComuns = {
          atividadeId: atv.id,
          source: 'atividade',
          userName,
          tipo: atv.tipo,
          status: atv.completed ? 'concluido' : atv.status,
          completed: !!atv.completed,
          empresa: atv.empresa,
          horaInicio,
          horaFim,
          observacao: atv.observacao || '',
          ligarPipeline: !!atv.ligarPipeline,
          rangeStart: startISO,
          rangeEnd: endISO,
        };

        // Visão Dia/Semana + atividade com hora → evento com horário real
        if (isTimeGridView && horaInicio) {
          const startDT = moment(`${startISO}T${horaInicio}`, "YYYY-MM-DDTHH:mm", true);
          if (!startDT.isValid()) return;
          let endDT = horaFim
            ? moment(`${safeEnd.format(ISO_FMT)}T${horaFim}`, "YYYY-MM-DDTHH:mm", true)
            : null;
          if (!endDT || !endDT.isValid() || !endDT.isAfter(startDT)) {
            endDT = startDT.clone().add(1, "hour");
          }
          segs.push({
            id: `atv-${atv.id}`,
            title: atv.nome || 'Atividade',
            start: startDT.format("YYYY-MM-DDTHH:mm:ss"),
            end: endDT.format("YYYY-MM-DDTHH:mm:ss"),
            allDay: false,
            extendedProps: {
              ...propsComuns,
              segIndex: 0,
              segCount: 1,
              showLabel: true,
              showDelete: true,
              color,
            },
          });
          return;
        }

        for (let i = 0; i < dayCount; i++) {
          const day = start.clone().add(i, "days").format(ISO_FMT);
          const showLabel = i === 0;            // só o 1º dia mostra o nome
          const showDelete = i === dayCount - 1; // marca o último dia (fim da barra)

          segs.push({
            id: `atv-${atv.id}:${day}`,
            title: atv.nome || 'Atividade',
            start: day,
            end: moment(day, ISO_FMT, true).add(1, "day").format(ISO_FMT),
            allDay: true,
            extendedProps: {
              ...propsComuns,
              segIndex: i,
              segCount: dayCount,
              showLabel,
              showDelete,
              color,
            },
          });
        }
      });

    // Compromissos do Pipeline (AG_LEAVES) → eventos com horário (azul).
    (compromissos || [])
      .filter(comp => comp.data)
      .forEach(comp => {
        const horaIni = comp.horaInicio || comp.hora || '09:00';
        const start = moment(`${comp.data}T${horaIni}`, "YYYY-MM-DDTHH:mm", true);
        if (!start.isValid()) return;
        // Fim: usa horaFim quando existir; senão cai na duração (compat)
        let end;
        if (comp.horaFim) {
          end = moment(`${comp.dataFim || comp.data}T${comp.horaFim}`, "YYYY-MM-DDTHH:mm", true);
          if (!end.isValid() || end.isSameOrBefore(start)) end = start.clone().add(60, "minutes");
        } else {
          const dur = Number(comp.duracao) > 0 ? Number(comp.duracao) : 60;
          end = start.clone().add(dur, "minutes");
        }

        segs.push({
          id: `comp-${comp.id}`,
          title: comp.titulo || 'Compromisso',
          start: start.format("YYYY-MM-DDTHH:mm:ss"),
          end: end.format("YYYY-MM-DDTHH:mm:ss"),
          allDay: false,
          extendedProps: {
            source: 'pipeline',
            compromissoId: comp.id,
            cardId: comp.cardId,
            userName: comp.responsavel || 'Comercial',
            tipo: comp.tipo,
            empresa: comp.cardEmpresa,
            horaInicio: horaIni,
            horaFim: comp.horaFim || end.format("HH:mm"),
            local: comp.local || '',
            observacao: comp.descricao || '',
            concluido: !!comp.concluido,
            rangeStart: comp.data,
            rangeEnd: comp.dataFim || comp.data,
            color: '#4299E1', // azul → distingue dos eventos de atividade
            showLabel: true,
            showDelete: true,
          },
        });
      });

    return segs;
  }, [atividades, compromissos, isTimeGridView]);

  const calendarApi = () => calendarRef.current?.getApi?.();

  const syncTitle = () => {
    const api = calendarApi();
    if (!api) return;
    setTimeout(() => setViewTitle(formatViewTitle(api.view?.title || "")), 0);
  };

  const changeView = (name) => {
    const api = calendarApi();
    if (!api) return;
    api.changeView(name);
    syncTitle();
  };

  // Quebra um valor do FullCalendar em data (YYYY-MM-DD) + hora (HH:mm, se houver).
  // Nas visões Dia/Semana, o clique num slot traz o horário junto
  // (ex.: "2026-07-23T09:00:00-03:00") — a hora é aproveitada no formulário.
  const splitDataHora = (value) => {
    if (!value) return { data: null, hora: null };
    const s = String(value);
    const m = s.match(/T(\d{2}):(\d{2})/);
    return { data: toISO(s), hora: m ? `${m[1]}:${m[2]}` : null };
  };

  // Criar evento = abre o MESMO modal completo da tela de Atividades (DRY).
  // Navega para /comercial/atividades com os parâmetros que disparam o modal de criação.
  const openCreateEvent = (start, end = start) => {
    const ini = splitDataHora(start);
    const fim = splitDataHora(end);
    const dataInicio = ini.data || hojeISO;
    // Bloqueio de Ação Retroativa (#6/#8): não cria em data passada
    if (dataInicio < hojeISO) return;
    const params = new URLSearchParams({ new: '1', date: dataInicio });
    const dataFim = fim.data && fim.data !== dataInicio ? fim.data : null;
    if (dataFim) params.set('dateFim', dataFim);
    // Hora clicada/selecionada na grade → pré-preenche o horário da atividade
    if (ini.hora && ini.hora !== '00:00') {
      params.set('hora', ini.hora);
      const horaFim = (fim.hora && (fim.hora !== ini.hora || dataFim))
        ? fim.hora
        : moment(`${dataInicio}T${ini.hora}`, 'YYYY-MM-DDTHH:mm').add(1, 'hour').format('HH:mm');
      if (horaFim && horaFim !== '00:00') params.set('horaFim', horaFim);
    }
    navigate(`/comercial/atividades?${params.toString()}`);
  };

  const eventClassNames = (arg) => {
    const classes = ["nogle-off-event"];
    const showLabel = !!arg?.event?.extendedProps?.showLabel;
    const showDelete = !!arg?.event?.extendedProps?.showDelete;

    if (showLabel && showDelete) classes.push("nogle-seg-single");
    else if (showLabel) classes.push("nogle-seg-start");
    else if (showDelete) classes.push("nogle-seg-end");
    else classes.push("nogle-seg-mid");

    return classes;
  };

  const eventContent = (arg) => {
    const { userName, color, showLabel, tipo, empresa, horaInicio, horaFim, status } = arg.event.extendedProps;
    const style = color ? { "--user-event-color": color } : {};

    const hora = horaInicio ? `${horaInicio}${horaFim && horaFim !== horaInicio ? `–${horaFim}` : ''}` : null;
    const metaParts = [tipo, userName, empresa].filter(Boolean);
    const concluida = String(status || '').toLowerCase().includes('conclu');
    const cancelada = String(status || '').toLowerCase().includes('cancel');
    const tooltip = [
      arg.event.title,
      [tipo, hora].filter(Boolean).join(' · '),
      userName ? `Responsável: ${userName}` : null,
      empresa ? `Empresa: ${empresa}` : null,
      concluida ? 'Concluída' : cancelada ? 'Cancelada' : null,
    ].filter(Boolean).join('\n');

    const cardClass = cancelada ? ' nogle-event-cancelled' : '';

    // Segmentos de continuação (não-início) só preenchem a barra, sem texto,
    // para a barra parecer contínua atravessando os dias.
    if (!showLabel) {
      return <div className={`fc-event-custom-card nogle-seg-fill${cardClass}`} style={style} title={tooltip} />;
    }

    return (
      <div className={`fc-event-custom-card nogle-seg-first nogle-event-rich${cardClass}`} style={style} title={tooltip}>
        <div className="fc-event-user-bubble">{String(userName || "N").charAt(0).toUpperCase()}</div>
        <div className="nogle-event-body">
          <div className="nogle-event-topline">
            <span className="nogle-event-name">{arg.event.title}</span>
            {concluida && (
              <span className="nogle-event-check" aria-label="Concluída">
                <AiOutlineCheckCircle />
              </span>
            )}
            {hora && <span className="nogle-event-hora">{hora}</span>}
          </div>
          {metaParts.length > 0 && <div className="nogle-event-meta">{metaParts.join(' · ')}</div>}
        </div>
      </div>
    );
  };

  const dayCellClassNames = (arg) => {
    const classes = [];
    if (moment(arg.date).isSame(moment(), "day")) classes.push("fc-day-today-custom");
    if (getHolidayByDate(toISO(arg.date))) classes.push("fc-day-holiday");

    return classes;
  };

  const mountHoliday = (arg) => {
    const dateStr = toISO(arg.date);
    const holiday = getHolidayByDate(dateStr);
    const frame = arg.el.querySelector(".fc-daygrid-day-frame");

    if (!holiday || !frame || frame.querySelector(".holiday-overlay")) return;

    const overlay = document.createElement("button");
    overlay.type = "button";
    overlay.className = "holiday-overlay";
    overlay.innerHTML = `
      <span class="holiday-icon-el"><svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><path d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V10h14v10zm0-12H5V6h14v2z"/></svg></span>
      <span class="holiday-text-el">${holiday.title}</span>
    `;
    overlay.addEventListener("pointerdown", (event) => event.stopPropagation());
    overlay.addEventListener("click", (event) => {
      event.stopPropagation();
      const rect = overlay.getBoundingClientRect();
      setHolidayPopover({
        title: holiday.title,
        date: dateStr,
        x: rect.left + rect.width / 2,
        y: rect.bottom + 8,
      });
    });

    frame.appendChild(overlay);
  };

  return (
    <Box className="calendar-shell">
      <Flex className="calendar-toolbar" align="center" justify="space-between" gap={4} wrap="wrap">
        <HStack spacing={3}>
          <Button className="calendar-btn" leftIcon={<Icon as={AiOutlineLeft} />} onClick={() => { calendarApi()?.prev(); syncTitle(); }}>
            Anterior
          </Button>
          <Button className="calendar-btn" onClick={() => { calendarApi()?.today(); syncTitle(); }}>
            Hoje
          </Button>
          <Button className="calendar-btn" rightIcon={<Icon as={AiOutlineRight} />} onClick={() => { calendarApi()?.next(); syncTitle(); }}>
            Proximo
          </Button>
        </HStack>

        <Text className="calendar-title">{viewTitle}</Text>

        <HStack spacing={3}>
          <Button className="calendar-create-btn" leftIcon={<Icon as={AiOutlineCalendar} />} onClick={() => navigate('/comercial/atividades?new=1')}>
            Criar evento
          </Button>
          <Button className="calendar-btn" onClick={() => changeView("dayGridMonth")}>
            Mes
          </Button>
          <Button className="calendar-btn" onClick={() => changeView("timeGridWeek")}>
            Semana
          </Button>
          <Button className="calendar-btn" onClick={() => changeView("timeGridDay")}>
            Dia
          </Button>
        </HStack>
      </Flex>

      <Box className="calendar-wrapper">
        <Box className="calendar-stage my-fullcalendar">
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]}
            initialView="dayGridMonth"
            initialDate={initialDate}
            events={events}
            eventClassNames={eventClassNames}
            eventContent={eventContent}
            eventDidMount={(arg) => {
              const color = arg.event.extendedProps?.color;
              if (color) arg.el.style.setProperty("--user-event-color", color);
            }}
            eventClick={(arg) => {
              // Impede que o clique no card propague para a célula do dia (que abriria a criação)
              arg.jsEvent?.stopPropagation?.();
              arg.jsEvent?.preventDefault?.();
              const props = arg.event.extendedProps || {};
              if (props.source === 'atividade' && props.atividadeId) {
                navigate(`/comercial/atividades?openActivityId=${encodeURIComponent(String(props.atividadeId))}&mode=view`);
                return;
              }
              setSelectedEvent(arg.event);
              onDetailsOpen();
            }}
            dateClick={(arg) => {
              // Não abre criação ao clicar num dia de feriado (mostra só o popover do feriado)
              if (getHolidayByDate(toISO(arg.date))) return;
              openCreateEvent(arg.dateStr, arg.dateStr);
            }}
            select={(selectInfo) => {
              // Seleção com horário (grade Dia/Semana): passa início/fim crus —
              // o openCreateEvent extrai data + hora e pré-preenche o formulário.
              if (selectInfo.allDay === false) {
                openCreateEvent(selectInfo.startStr, selectInfo.endStr);
                return;
              }
              const start = toISO(selectInfo.startStr) || toISO(selectInfo.start);
              const exclusiveEnd = toISO(selectInfo.endStr) || toISO(selectInfo.end) || start;
              const end = moment(exclusiveEnd, ISO_FMT, true).subtract(1, "day").format(ISO_FMT);
              openCreateEvent(start, moment(end, ISO_FMT, true).isBefore(moment(start, ISO_FMT, true)) ? start : end);
            }}
            datesSet={(dateInfo) => {
              setViewTitle(formatViewTitle(dateInfo.view?.title || ""));
              setViewName(dateInfo.view?.type || 'dayGridMonth');
            }}
            dayCellClassNames={dayCellClassNames}
            dayCellDidMount={mountHoliday}
            height="calc(100vh - 230px)"
            headerToolbar={false}
            dayMaxEventRows={999}
            eventDisplay="block"
            eventOrder="extendedProps.rangeStart,start,-duration"
            locale={ptBrLocale}
            moreLinkClick="popover"
            selectable
          />
        </Box>
      </Box>

      {holidayPopover ? (
        <>
          <Box className="calendar-popover-backdrop" onClick={() => setHolidayPopover(null)} />
          <Box
            className="calendar-holiday-popover"
            style={{
              left: Math.max(8, Math.min(holidayPopover.x - 140, window.innerWidth - 300)),
              top: holidayPopover.y,
            }}
          >
            <Flex justify="space-between" align="flex-start">
              <Text className="calendar-popover-badge">Feriado nacional</Text>
              <Button className="calendar-popover-close" onClick={() => setHolidayPopover(null)} aria-label="Fechar">
                <Icon as={AiOutlineClose} />
              </Button>
            </Flex>
            <Box>
              <Text className="calendar-popover-title">{holidayPopover.title}</Text>
              <Text className="calendar-popover-date">{fmtBR(holidayPopover.date)}</Text>
            </Box>
          </Box>
        </>
      ) : null}

      <Modal isOpen={detailsOpen} onClose={onDetailsClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent bg="surface" borderRadius="lg" borderColor="borderPrimary" borderWidth="1px">
          <ModalHeader color="textPrimary" borderBottomWidth="1px" borderColor="borderPrimary">
            <HStack>
              <Icon as={AiOutlineCalendar} color="secondary" />
              <Text>Detalhes do compromisso</Text>
            </HStack>
          </ModalHeader>
          <ModalBody py={4}>
            {(() => {
              const p = selectedEvent?.extendedProps || {};
              const color = p.color || '#01E975';
              const inicio = `${fmtBR(p.rangeStart)}${p.horaInicio ? ` · ${p.horaInicio}` : ''}`;
              const fim = `${fmtBR(p.rangeEnd)}${p.horaFim ? ` · ${p.horaFim}` : ''}`;
              return (
                <VStack align="stretch" spacing={5}>
                  {p.source === 'pipeline' && (
                    <Box bg="rgba(66, 153, 225, 0.12)" border="1px solid rgba(66, 153, 225, 0.3)" borderRadius="md" p={3}>
                      <HStack spacing={2}>
                        <Icon as={AiOutlinePartition} color="#4299E1" />
                        <Text fontSize="xs" color="#4299E1" fontWeight={900} textTransform="uppercase" letterSpacing="1.5px">
                          Compromisso do Pipeline
                        </Text>
                      </HStack>
                    </Box>
                  )}

                  {/* Título + badges */}
                  <Box>
                    <Text fontSize="10px" color="textMuted" textTransform="uppercase" letterSpacing="1.5px" fontWeight="800" mb={1}>
                      Compromisso
                    </Text>
                    <Text fontWeight={900} fontSize="xl" color="textPrimary" lineHeight="1.2">
                      {selectedEvent?.title || "-"}
                    </Text>
                    <HStack spacing={2} mt={3} flexWrap="wrap">
                      {p.tipo && (
                        <Badge px={2.5} py={1} borderRadius="md" bg={`${color}22`} color={color} border="1px solid" borderColor={`${color}55`} fontWeight="800" textTransform="none">
                          {p.tipo}
                        </Badge>
                      )}
                    </HStack>
                  </Box>

                  <Divider borderColor="borderPrimary" />

                  {/* Informações complementares */}
                  <SimpleGrid columns={2} spacingX={4} spacingY={4}>
                    <InfoField icon={AiOutlineUser} label="Responsável" value={p.userName} />
                    <InfoField icon={AiOutlineShop} label="Empresa" value={p.empresa} />
                    <InfoField icon={AiOutlineClockCircle} label="Início" value={inicio} />
                    <InfoField icon={AiOutlineClockCircle} label="Fim" value={fim} />
                  </SimpleGrid>

                  {p.local ? (
                    <InfoField icon={AiOutlineEnvironment} label="Local / Link" value={p.local} />
                  ) : null}

                  {p.observacao ? (
                    <Box>
                      <Text fontSize="10px" color="textMuted" textTransform="uppercase" letterSpacing="1px" fontWeight="800" mb={1}>
                        Observação
                      </Text>
                      <Box p={3} bg="surfaceRaised" borderRadius="md" border="1px solid" borderColor="borderPrimary">
                        <Text color="textSecondary" fontSize="sm" whiteSpace="pre-wrap">{p.observacao}</Text>
                      </Box>
                    </Box>
                  ) : null}
                </VStack>
              );
            })()}
          </ModalBody>
          {(() => {
            const p = selectedEvent?.extendedProps || {};
            if (p.source !== 'pipeline') return null;
            const fimISO = toISO(p.rangeEnd) || toISO(p.rangeStart);
            const passado = fimISO ? fimISO < hojeISO : false;

            const handleExcluir = () => {
              if (passado) return;
              onDelOpen();
            };



            return (
              <ModalFooter borderTopWidth="1px" borderColor="borderPrimary" gap={2} rowGap={2} flexWrap="wrap">
                <Button
                  leftIcon={<Icon as={AiOutlineDelete} />}
                  variant="ghost"
                  color="red.400"
                  size="sm"
                  fontWeight="800"
                  flexShrink={0}
                  isDisabled={passado}
                  title={passado ? 'Eventos passados não podem ser excluídos' : undefined}
                  _hover={{ bg: 'rgba(229,62,62,0.1)' }}
                  onClick={handleExcluir}
                >
                  Excluir
                </Button>
                <Box flex={1} />
                <Button variant="ghost" color="textSecondary" size="sm" flexShrink={0} onClick={onDetailsClose}>
                  Fechar
                </Button>
              </ModalFooter>
            );
          })()}
        </ModalContent>
      </Modal>

      {/* Confirmação padrão de exclusão */}
      <ConfirmDeleteDialog
        isOpen={isDelOpen}
        onClose={onDelClose}
        onConfirm={handleConfirmarExcluirEvento}
        title="Excluir Compromisso"
        itemName={selectedEvent?.title}
        warning={selectedEvent?.extendedProps?.cardId && !!(pipelineCards || []).find(c => String(c.id) === String(selectedEvent.extendedProps.cardId))}
        description={(() => {
          if (!selectedEvent?.extendedProps?.cardId) return "";
          const negocioVinculado = (pipelineCards || []).find(c => String(c.id) === String(selectedEvent.extendedProps.cardId));
          return negocioVinculado 
            ? `Atenção: Este compromisso está vinculado ao negócio "${negocioVinculado.titulo}". Ao excluir, este compromisso será desvinculado.`
            : "";
        })()}
      />
    </Box>
  );
}
